/* eslint-disable max-lines -- verbatim extraction from admin-dashboard-page.tsx; split further when the card is next reworked */
import { useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { ToolConfig } from "@/api/admin-api"

import {
  getToolActions,
  getToolConfig,
  updateToolConfig,
  updateToolsStatusBatch,
} from "@/api/admin-api"
import { Shield, ShieldCheck, Wrench } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"

import { AccessPolicyDialog } from "./access-policy-dialog"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { DelegationModeControl } from "./delegation-mode-control"
import { EditToolDialog } from "./edit-tool-dialog"
import { ToolConfigDialog } from "./tool-config-dialog"
import { ToolsTable } from "./tools-table"
import { TranslatorAccessManagement } from "./translator-access-management"
import {
  useAdminAgentsQuery,
  useAdminAgentToolsQuery,
  useAdminToolAccessPolicyQuery,
  useAdminToolActionAccessPolicyQuery,
  useAdminToolsBaseAccessPolicyQuery,
  useClearToolAccessPolicyMutation,
  useUpdateActionsStatusBatchMutation,
  useUpdateActionStatusMutation,
  useUpdateToolAccessPolicyMutation,
  useUpdateToolActionAccessPolicyMutation,
  useUpdateToolsBaseAccessPolicyMutation,
  useUpdateToolStatusMutation,
} from "../hooks/admin-queries"
import { isPolicyConfigured } from "../utils/access-policy"

export function ToolsManagementCard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [updatingToolName, setUpdatingToolName] = useState<string | undefined>()
  const [editingToolName, setEditingToolName] = useState<string | null>(null)
  const [isUpdatingAll, setIsUpdatingAll] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<string>("explorer")
  const [loadingActionsFor, setLoadingActionsFor] = useState<string | undefined>()
  const [toolActions, setToolActions] = useState<
    Record<
      string,
      Array<{ name: string; description: string; enabled: boolean; access_configured?: boolean }>
    >
  >({})
  const [updatingAction, setUpdatingAction] = useState<string | undefined>()
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false)
  const [isBaseAccessDialogOpen, setIsBaseAccessDialogOpen] = useState(false)
  const [accessTarget, setAccessTarget] = useState<
    | { kind: "tool"; toolName: string }
    | { kind: "action"; toolName: string; actionName: string }
    | null
  >(null)
  const [configDialogTool, setConfigDialogTool] = useState<string | null>(null)
  const [configDialogValue, setConfigDialogValue] = useState<ToolConfig | null>(null)
  const [isLoadingToolConfig, setIsLoadingToolConfig] = useState(false)
  const [isSavingToolConfig, setIsSavingToolConfig] = useState(false)

  const { data: agents, isLoading: isLoadingAgents } = useAdminAgentsQuery()
  const { data: tools, isLoading: isLoadingTools } = useAdminAgentToolsQuery(selectedAgent)
  const {
    data: baseAccessPolicy,
    isLoading: isLoadingBaseAccessPolicy,
    isError: isBaseAccessPolicyError,
  } = useAdminToolsBaseAccessPolicyQuery(selectedAgent, true)
  const updateMutation = useUpdateToolStatusMutation()
  const updateActionMutation = useUpdateActionStatusMutation()
  const updateActionsBatchMutation = useUpdateActionsStatusBatchMutation()
  const updateToolAccessMutation = useUpdateToolAccessPolicyMutation()
  const updateToolActionAccessMutation = useUpdateToolActionAccessPolicyMutation()
  const clearToolAccessMutation = useClearToolAccessPolicyMutation()
  const updateBaseAccessMutation = useUpdateToolsBaseAccessPolicyMutation()

  const toolAccessQuery = useAdminToolAccessPolicyQuery(
    selectedAgent,
    accessTarget?.kind === "tool" ? accessTarget.toolName : undefined,
    isAccessDialogOpen && accessTarget?.kind === "tool"
  )
  const toolActionAccessQuery = useAdminToolActionAccessPolicyQuery({
    agentName: selectedAgent,
    toolName: accessTarget?.kind === "action" ? accessTarget.toolName : undefined,
    actionName: accessTarget?.kind === "action" ? accessTarget.actionName : undefined,
    enabled: isAccessDialogOpen && accessTarget?.kind === "action",
  })

  const accessPolicy =
    accessTarget?.kind === "tool" ? toolAccessQuery.data : toolActionAccessQuery.data
  const isLoadingAccessPolicy =
    accessTarget?.kind === "tool" ? toolAccessQuery.isLoading : toolActionAccessQuery.isLoading
  const isAccessPolicyError =
    accessTarget?.kind === "tool" ? toolAccessQuery.isError : toolActionAccessQuery.isError
  const isSavingAccessPolicy =
    accessTarget?.kind === "tool"
      ? updateToolAccessMutation.isPending || clearToolAccessMutation.isPending
      : updateToolActionAccessMutation.isPending

  const accessDialogName = useMemo(() => {
    if (!accessTarget) return selectedAgent
    if (accessTarget.kind === "tool") return `${selectedAgent}/${accessTarget.toolName}`
    return `${selectedAgent}/${accessTarget.toolName}:${accessTarget.actionName}`
  }, [accessTarget, selectedAgent])

  const isLoading = isLoadingAgents || isLoadingTools
  const baseAccessConfigured = useMemo(() => {
    return isPolicyConfigured(baseAccessPolicy)
  }, [baseAccessPolicy])

  const selectedTool = useMemo(() => {
    if (!tools || !accessTarget || accessTarget.kind !== "tool") return undefined
    return tools.find((t) => t.name === accessTarget.toolName)
  }, [accessTarget, tools])
  const inheritByDefault = selectedTool?.access_overridden !== true

  const handleToggleStatus = async (toolName: string, enabled: boolean) => {
    setUpdatingToolName(toolName)
    try {
      await updateMutation.mutateAsync({ agentName: selectedAgent, toolName, enabled })
    } catch {
      // Error handled by React Query mutation
    } finally {
      setUpdatingToolName(undefined)
    }
  }

  const handleToggleAll = async (enabled: boolean) => {
    if (!tools || tools.length === 0) return

    setIsUpdatingAll(true)

    await queryClient.cancelQueries({ queryKey: ["admin", "agents", selectedAgent, "tools"] })

    const currentTools =
      queryClient.getQueryData<typeof tools>(["admin", "agents", selectedAgent, "tools"]) || tools

    const previousTools = currentTools
    const toolsToUpdate = currentTools.filter((tool) => tool.enabled !== enabled)

    queryClient.setQueryData(
      ["admin", "agents", selectedAgent, "tools"],
      currentTools.map((tool) => ({ ...tool, enabled }))
    )

    try {
      const updates = Object.fromEntries(toolsToUpdate.map((tool) => [tool.name, enabled]))
      await updateToolsStatusBatch(selectedAgent, updates)
    } catch {
      queryClient.setQueryData(["admin", "agents", selectedAgent, "tools"], previousTools)
      await queryClient.invalidateQueries({ queryKey: ["admin", "agents", selectedAgent, "tools"] })
    } finally {
      setIsUpdatingAll(false)
    }
  }

  const handleLoadActions = async (toolName: string) => {
    if (toolName in toolActions) return // Already loaded

    const agentAtRequest = selectedAgent
    setLoadingActionsFor(toolName)
    try {
      const actions = await getToolActions(agentAtRequest, toolName)
      // Only update state if we're still on the same agent
      if (agentAtRequest === selectedAgent) {
        setToolActions((prev) => ({ ...prev, [toolName]: actions }))
      }
    } catch {
      // Failed to load actions - state remains unchanged
    } finally {
      setLoadingActionsFor(undefined)
    }
  }

  const handleToggleAction = async (toolName: string, actionName: string, enabled: boolean) => {
    setUpdatingAction(actionName)

    // Optimistic update
    setToolActions((prev) => ({
      ...prev,
      [toolName]: (prev[toolName] ?? []).map((a) =>
        a.name === actionName ? { ...a, enabled } : a
      ),
    }))

    try {
      await updateActionMutation.mutateAsync({
        agentName: selectedAgent,
        toolName,
        actionName,
        enabled,
      })
    } catch {
      // Revert optimistic update
      setToolActions((prev) => ({
        ...prev,
        [toolName]: (prev[toolName] ?? []).map((a) =>
          a.name === actionName ? { ...a, enabled: !enabled } : a
        ),
      }))
    } finally {
      setUpdatingAction(undefined)
    }
  }

  const handleToggleAllActions = async (toolName: string, enabled: boolean) => {
    const actions = toolActions[toolName]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Record type doesn't account for missing keys
    if (!actions || actions.length === 0) return

    // Optimistic update
    const previousActions = [...actions]
    setToolActions((prev) => ({
      ...prev,
      [toolName]: (prev[toolName] ?? []).map((a) => ({ ...a, enabled })),
    }))

    try {
      const updates = Object.fromEntries(actions.map((a) => [a.name, enabled]))
      await updateActionsBatchMutation.mutateAsync({
        agentName: selectedAgent,
        toolName,
        updates,
      })
    } catch {
      // Revert optimistic update
      setToolActions((prev) => ({
        ...prev,
        [toolName]: previousActions,
      }))
    }
  }

  const allEnabledState = useMemo(() => {
    if (!tools || tools.length === 0) return false
    const enabledCount = tools.filter((tool) => tool.enabled).length
    if (enabledCount === 0) return false
    if (enabledCount === tools.length) return true
    return undefined
  }, [tools])

  const handleOpenToolConfig = async (toolName: string) => {
    setConfigDialogTool(toolName)
    setConfigDialogValue(null)
    setIsLoadingToolConfig(true)
    try {
      const config = await getToolConfig(selectedAgent, toolName)
      setConfigDialogValue(config)
    } catch {
      toast.error(
        t("adminDashboard.tools.config.loadError", {
          defaultValue: "Failed to load tool configuration",
        })
      )
      setConfigDialogValue({})
    } finally {
      setIsLoadingToolConfig(false)
    }
  }

  const handleSaveToolConfig = async (next: ToolConfig) => {
    if (!configDialogTool) return
    setIsSavingToolConfig(true)
    try {
      const saved = await updateToolConfig(selectedAgent, configDialogTool, next)
      setConfigDialogValue(saved)
      toast.success(
        t("adminDashboard.tools.config.saveSuccess", {
          defaultValue: "Tool configuration updated",
        })
      )
      setConfigDialogTool(null)
      setConfigDialogValue(null)
    } catch {
      toast.error(
        t("adminDashboard.tools.config.saveError", {
          defaultValue: "Failed to update tool configuration",
        })
      )
    } finally {
      setIsSavingToolConfig(false)
    }
  }

  return (
    <>
      <CollapsibleManagementSection
        icon={<Wrench className="size-5" />}
        id="tools"
        storageKey="admin-management-tools-expanded"
        title={t("adminDashboard.tools.title", "Tools Management")}
        headerActions={
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Select
              disabled={isLoading || isUpdatingAll}
              value={selectedAgent}
              onValueChange={(value) => {
                setSelectedAgent(value)
                setToolActions({}) // Clear actions when agent changes
                setIsAccessDialogOpen(false)
                setIsBaseAccessDialogOpen(false)
                setAccessTarget(null)
              }}
            >
              <SelectTrigger aria-label="Agent" className="w-40">
                <SelectValue placeholder="Select agent" />
              </SelectTrigger>
              <SelectContent>
                {agents?.map((agent) => (
                  <SelectItem key={agent} value={agent}>
                    {agent}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="flex items-center gap-2 px-3"
              disabled={isLoading || isUpdatingAll}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setIsBaseAccessDialogOpen(true)}
            >
              {baseAccessConfigured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
              {t("adminDashboard.tools.baseAccess", { defaultValue: "Access for all" })}
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">
                {t("adminDashboard.tools.enableAll", "Enable All")}
              </span>
              <Switch
                aria-label={t("adminDashboard.tools.enableAll", "Enable All")}
                checked={allEnabledState === true}
                disabled={isLoading || isUpdatingAll || !tools || tools.length === 0}
                onCheckedChange={handleToggleAll}
              />
            </div>
            <DelegationModeControl capability="tools" />
          </div>
        }
      >
        {selectedAgent === "explorer" && <TranslatorAccessManagement />}
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex items-center justify-between rounded-lg border p-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-12" />
              </div>
            ))}
          </div>
        ) : (
          <ToolsTable
            agentName={selectedAgent}
            disableAll={isUpdatingAll}
            isUpdating={updatingToolName}
            isUpdatingAction={updatingAction}
            loadingActionsFor={loadingActionsFor}
            toolActions={toolActions}
            tools={tools || []}
            onEditTool={(toolName) => setEditingToolName(toolName)}
            onLoadActions={handleLoadActions}
            onToggleAction={handleToggleAction}
            onToggleAllActions={handleToggleAllActions}
            onToggleStatus={handleToggleStatus}
            onEditActionAccess={(toolName, actionName) => {
              setAccessTarget({ kind: "action", toolName, actionName })
              setIsAccessDialogOpen(true)
            }}
            onEditToolAccess={(toolName) => {
              setAccessTarget({ kind: "tool", toolName })
              setIsAccessDialogOpen(true)
            }}
            onEditToolConfig={(toolName) => {
              void handleOpenToolConfig(toolName)
            }}
          />
        )}
      </CollapsibleManagementSection>

      <ToolConfigDialog
        config={configDialogValue}
        isLoading={isLoadingToolConfig}
        isSaving={isSavingToolConfig}
        open={configDialogTool !== null}
        toolName={configDialogTool ?? ""}
        onSave={handleSaveToolConfig}
        onOpenChange={(open) => {
          if (!open) {
            setConfigDialogTool(null)
            setConfigDialogValue(null)
          }
        }}
      />

      <EditToolDialog
        agentName={selectedAgent}
        isOpen={editingToolName !== null}
        toolName={editingToolName}
        onOpenChange={(open) => {
          if (!open) setEditingToolName(null)
        }}
      />

      <AccessPolicyDialog
        isLoading={isLoadingBaseAccessPolicy}
        isSaving={updateBaseAccessMutation.isPending}
        loadError={isBaseAccessPolicyError}
        open={isBaseAccessDialogOpen}
        policy={baseAccessPolicy}
        description={t("adminDashboard.tools.baseAccessDescription", {
          defaultValue:
            "Default access policy applied to all tools for this agent unless overridden per tool.",
        })}
        title={t("adminDashboard.tools.baseAccessTitle", {
          defaultValue: "Base access policy — Tools",
        })}
        onOpenChange={setIsBaseAccessDialogOpen}
        onSave={async (policy) => {
          try {
            await updateBaseAccessMutation.mutateAsync({ agentName: selectedAgent, policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <AccessPolicyDialog
        isLoading={isLoadingAccessPolicy}
        isSaving={isSavingAccessPolicy}
        loadError={isAccessPolicyError || isBaseAccessPolicyError}
        open={isAccessDialogOpen}
        policy={accessPolicy}
        description={t("adminDashboard.access.dialogDescription", {
          defaultValue: "Configure who can access this resource.",
        })}
        inherit={
          accessTarget?.kind === "tool"
            ? {
                enabled: true,
                inheritByDefault,
                policy: baseAccessPolicy,
                label: t("adminDashboard.tools.inheritBaseAccess", {
                  defaultValue: "Inherit access for all",
                }),
                description: t("adminDashboard.tools.inheritBaseAccessDescription", {
                  defaultValue:
                    "When enabled, this tool uses the section baseline access policy. Disable to override for this tool.",
                }),
                onSaveInherited: async () => {
                  await clearToolAccessMutation.mutateAsync({
                    agentName: selectedAgent,
                    toolName: accessTarget.toolName,
                  })
                  void queryClient.invalidateQueries({
                    queryKey: ["admin", "agents", selectedAgent, "tools"],
                  })
                },
              }
            : undefined
        }
        title={t("adminDashboard.access.dialogTitle", {
          defaultValue: "Access policy — {{name}}",
          name: accessDialogName,
        })}
        onOpenChange={(open) => {
          setIsAccessDialogOpen(open)
          if (!open) {
            setAccessTarget(null)
          }
        }}
        onSave={async (policy) => {
          if (!accessTarget) return
          try {
            if (accessTarget.kind === "tool") {
              await updateToolAccessMutation.mutateAsync({
                agentName: selectedAgent,
                toolName: accessTarget.toolName,
                policy,
              })
            } else {
              await updateToolActionAccessMutation.mutateAsync({
                agentName: selectedAgent,
                toolName: accessTarget.toolName,
                actionName: accessTarget.actionName,
                policy,
              })
            }
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />
    </>
  )
}
